"use strict";
/*
 * Copyright 2024, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.VisitedPlace = exports.visitedPlaceAttributesWithComposedAttributes = exports.visitedPlaceAttributes = void 0;
const omit_1 = __importDefault(require("lodash/omit"));
const IValidatable_1 = require("./IValidatable");
const CompletableAttributes_1 = require("./attributeTypes/CompletableAttributes");
const SurveyObject_1 = require("./SurveyObject");
const Weight_1 = require("./Weight");
const Uuidable_1 = require("./Uuidable");
const Place_1 = require("./Place");
const ParamsValidatorUtils_1 = require("../../utils/ParamsValidatorUtils");
const Result_type_1 = require("../../types/Result.type");
const StartEndable_1 = require("./StartEndable");
const ConstructorUtils_1 = require("../../utils/ConstructorUtils");
const SurveyObjectUnserializer_1 = require("./SurveyObjectUnserializer");
exports.visitedPlaceAttributes = [
    ...StartEndable_1.startEndDateAndTimesAttributes,
    ...Weight_1.weightableAttributeNames,
    ...IValidatable_1.validatableAttributeNames,
    ...Uuidable_1.uuidableAttributeNames,
    ...CompletableAttributes_1.completableAttributeNames,
    '_sequence',
    'activity',
    'activityCategory',
    'activityOtherSpecify',
    'isShortcut',
    'preData'
];
exports.visitedPlaceAttributesWithComposedAttributes = [...exports.visitedPlaceAttributes, '_place'];
/**
 * A visited place is a location that has been visited during a trip/journey
 * and that has an activity.
 * It could be home, a work place, a school place, a restaurant, a place of leisure,
 * a shopping place, etc.
 */
class VisitedPlace extends SurveyObject_1.SurveyObject {
    constructor(params, surveyObjectsRegistry) {
        super(params._uuid);
        this._surveyObjectsRegistry = surveyObjectsRegistry;
        this._attributes = {};
        this._customAttributes = {};
        const { attributes, customAttributes } = ConstructorUtils_1.ConstructorUtils.initializeAttributes((0, omit_1.default)(params, ['_place', '_journeyUuid']), exports.visitedPlaceAttributes, exports.visitedPlaceAttributesWithComposedAttributes);
        this._attributes = attributes;
        this._customAttributes = customAttributes;
        this.place = ConstructorUtils_1.ConstructorUtils.initializeComposedAttribute(params._place, Place_1.Place.unserialize, this._surveyObjectsRegistry);
        this.journeyUuid = params._journeyUuid;
        this._surveyObjectsRegistry.registerVisitedPlace(this);
    }
    get attributes() {
        return this._attributes;
    }
    get customAttributes() {
        return this._customAttributes;
    }
    get _weights() {
        return this._attributes._weights;
    }
    set _weights(value) {
        this._attributes._weights = value;
    }
    get _sequence() {
        return this._attributes._sequence;
    }
    set _sequence(value) {
        this._attributes._sequence = value;
    }
    get place() {
        return this._place;
    }
    set place(value) {
        this._place = value;
    }
    get startDate() {
        return this._attributes.startDate;
    }
    set startDate(value) {
        this._attributes.startDate = value;
    }
    get startTime() {
        return this._attributes.startTime;
    }
    set startTime(value) {
        this._attributes.startTime = value;
    }
    get startTimePeriod() {
        return this._attributes.startTimePeriod;
    }
    set startTimePeriod(value) {
        this._attributes.startTimePeriod = value;
    }
    get endDate() {
        return this._attributes.endDate;
    }
    set endDate(value) {
        this._attributes.endDate = value;
    }
    get endTime() {
        return this._attributes.endTime;
    }
    set endTime(value) {
        this._attributes.endTime = value;
    }
    get endTimePeriod() {
        return this._attributes.endTimePeriod;
    }
    set endTimePeriod(value) {
        this._attributes.endTimePeriod = value;
    }
    get activity() {
        return this._attributes.activity;
    }
    set activity(value) {
        this._attributes.activity = value;
    }
    get activityCategory() {
        return this._attributes.activityCategory;
    }
    set activityCategory(value) {
        this._attributes.activityCategory = value;
    }
    get activityOtherSpecify() {
        return this._attributes.activityOtherSpecify;
    }
    set activityOtherSpecify(value) {
        this._attributes.activityOtherSpecify = value;
    }
    get geography() {
        var _a;
        return (_a = this._place) === null || _a === void 0 ? void 0 : _a.geography;
    }
    get name() {
        var _a;
        return (_a = this._place) === null || _a === void 0 ? void 0 : _a.name;
    }
    get address() {
        var _a;
        return (_a = this._place) === null || _a === void 0 ? void 0 : _a.address;
    }
    /**
     * Whether this place was created from an interview shortcut.
     * The interview path itself is not kept on the survey object.
     */
    get isShortcut() {
        return this._attributes.isShortcut;
    }
    set isShortcut(value) {
        this._attributes.isShortcut = value;
    }
    get preData() {
        return this._attributes.preData;
    }
    set preData(value) {
        this._attributes.preData = value;
    }
    get journeyUuid() {
        return this._journeyUuid;
    }
    set journeyUuid(value) {
        this._journeyUuid = value;
    }
    get journey() {
        if (!this._journeyUuid) {
            return undefined;
        }
        return this._surveyObjectsRegistry.getJourney(this._journeyUuid);
    }
    get person() {
        var _a;
        return (_a = this.journey) === null || _a === void 0 ? void 0 : _a.person;
    }
    get household() {
        var _a;
        return (_a = this.person) === null || _a === void 0 ? void 0 : _a.household;
    }
    /**
     * Creates a VisitedPlace object from sanitized parameters
     * @param {ExtendedVisitedPlaceAttributes | SerializedExtendedVisitedPlaceAttributes} params - Sanitized visited place parameters
     * @returns {VisitedPlace} New VisitedPlace instance
     */
    static unserialize(params, surveyObjectsRegistry) {
        const flattenedParams = SurveyObjectUnserializer_1.SurveyObjectUnserializer.flattenSerializedData(params);
        return new VisitedPlace(flattenedParams, surveyObjectsRegistry);
    }
    static create(dirtyParams, surveyObjectsRegistry) {
        const errors = VisitedPlace.validateParams(dirtyParams);
        const visitedPlace = errors.length === 0 ? new VisitedPlace(dirtyParams, surveyObjectsRegistry) : undefined;
        if (errors.length > 0) {
            return (0, Result_type_1.createErrors)(errors);
        }
        return (0, Result_type_1.createOk)(visitedPlace);
    }
    static validateParams(dirtyParams, displayName = 'VisitedPlace') {
        const errors = [];
        // Validate params object:
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRequired('params', dirtyParams, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRecord('params', dirtyParams, displayName));
        // Validate _uuid:
        errors.push(...Uuidable_1.Uuidable.validateParams(dirtyParams));
        // Validate _isValid:
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isBoolean('_isValid', dirtyParams._isValid, displayName));
        errors.push(...SurveyObject_1.SurveyObject.validateCompletableParams(dirtyParams, displayName));
        errors.push(...(0, Weight_1.validateWeights)(dirtyParams._weights));
        // Validate StartEndable attributes:
        errors.push(...StartEndable_1.StartEndable.validateParams(dirtyParams, displayName));
        // Validate _sequence:
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isPositiveInteger('_sequence', dirtyParams._sequence, displayName));
        // Validate visited place specific attributes:
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('activity', dirtyParams.activity, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('activityCategory', dirtyParams.activityCategory, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('activityOtherSpecify', dirtyParams.activityOtherSpecify, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isBoolean('isShortcut', dirtyParams.isShortcut, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRecord('preData', dirtyParams.preData, displayName, false));
        // Validate composed place:
        const placeAttributes = dirtyParams._place;
        if (placeAttributes) {
            errors.push(...Place_1.Place.validateParams(placeAttributes, 'VisitedPlace Place'));
        }
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isUuid('_journeyUuid', dirtyParams._journeyUuid, displayName));
        return errors;
    }
}
exports.VisitedPlace = VisitedPlace;
VisitedPlace._confidentialAttributes = ['preData'];
//# sourceMappingURL=VisitedPlace.js.map